import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Globe } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../LanguageContext";

const Navbar = () => {
  const { lang, setLang } = useLang();
  const [langOpen, setLangOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("start");
  const langRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = useMemo(
    () => [
      { id: "start", label: { de: "Start", en: "Home", es: "Inicio" } },
      { id: "produkte", label: { de: "Produkte", en: "Products", es: "Productos" } },
      { id: "industrial-psu", label: { de: "Industrial PSU", en: "Industrial PSU", es: "Industrial PSU" } },
      { id: "rack-rails", label: { de: "Rack Rails", en: "Rack Rails", es: "Rack Rails" } },
      { id: "about", label: { de: "Über uns", en: "About us", es: "Sobre nosotros" } },
      { id: "contact", label: { de: "Kontakt", en: "Contact", es: "Contacto" } },
    ],
    []
  );

  const doScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
    }
  }, []);

  const scrollToSection = (id) => {
    setLangOpen(false);

    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }

    doScroll(id);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (location.pathname === "/" && location.state?.scrollTo) {
      const id = location.state.scrollTo;

      setTimeout(() => {
        doScroll(id);
        navigate("/", { replace: true, state: null });
      }, 100);
    }
  }, [location.pathname, location.state, navigate, doScroll]);

  useEffect(() => {
    if (location.pathname !== "/") return;

    const handleScroll = () => {
      const sections = navItems
        .map((item) => document.getElementById(item.id))
        .filter(Boolean);

      const scrollPosition = window.scrollY + 120;

      for (const section of sections) {
        const top = section.offsetTop;
        const bottom = top + section.offsetHeight;

        if (scrollPosition >= top && scrollPosition < bottom) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname, navItems]);

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <button
          onClick={() => scrollToSection("start")}
          className="flex items-center gap-3 text-left"
        >
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="FUM DA"
            className="w-24 h-auto object-contain"
          />
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`text-sm font-medium transition ${
                activeSection === item.id
                  ? "text-[#0E3A67]"
                  : "text-gray-700 hover:text-[#0E3A67]"
              }`}
            >
              {item.label[lang] || item.label.de}
            </button>
          ))}
        </nav>

        <div className="relative" ref={langRef}>
          <button
            onClick={() => setLangOpen((prev) => !prev)}
            className="flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-700 shadow-sm"
          >
            <Globe size={16} />
            {lang.toUpperCase()}
          </button>

          {langOpen && (
            <div className="absolute right-0 top-full z-[9999] mt-2 w-36 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
              {["de", "en", "es"].map((code) => (
                <button
                  key={code}
                  onClick={() => {
                    setLang(code);
                    setLangOpen(false);
                  }}
                  className={`block w-full px-4 py-3 text-left text-sm hover:bg-gray-50 ${
                    lang === code
                      ? "font-semibold text-[#0E3A67]"
                      : "text-gray-700"
                  }`}
                >
                  {code === "de" ? "Deutsch" : code === "en" ? "English" : "Español"}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
